<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useWikiPagesStore, type WikiPage } from '../stores/wiki_pages'
import { categoryLabel } from '../composables/categoryLabel'
import { isMemoryPage } from '../composables/categoryBucket'
import { useCategoryFilter } from '../composables/useCategoryFilter'
import { useSidebarSearch } from '../composables/useSidebarSearch'
import WikiSearchPanel from './WikiSearchPanel.vue'
import WikiSidebarSearchResults from './WikiSidebarSearchResults.vue'
import WikiPageCard from './WikiPageCard.vue'

// `/` landing — what the user sees when they aren't reading a specific
// page. Three sections:
//   1. Inline FTS search (delegated to WikiSearchPanel) — when active,
//      hides the cards/chips below so results take over.
//   2. Filter-by-category chip strip — distinct categories in the
//      catalog plus a "General" bucket for null-category pages.
//      Single-select; clicking the active chip clears.
//   3. Recently updated pages — capped card grid sorted by
//      `updated_at`. Filter narrows in place; URL stays at `/`.
//
// Catalog tree, breadcrumbs and `/c/:cat` are still reachable for
// deeper browsing. Reads the catalog directly from the wiki_pages
// store so the view stays reactive across ingest broadcasts.

const RECENT_PAGE_LIMIT = 12

const { pages } = storeToRefs(useWikiPagesStore())

// Memory is browsed exclusively through the dedicated /memory view,
// so the home browse surfaces — the chip strip and the
// recently-updated grid — exclude memory pages. Keeps operational
// memory notes out of the editorial "what did I touch lately" glance.
const browsablePages = computed<WikiPage[]>(() => pages.value.filter((p) => !isMemoryPage(p)))

const {
  chips: categoryChips,
  activeFilter,
  toggleFilter,
  clearFilter,
  filterPages,
  activeFilterLabel,
} = useCategoryFilter(browsablePages)

const recentPages = computed<WikiPage[]>(() => {
  const withTimestamps = browsablePages.value.filter((p) => Boolean(p.updated_at))
  const sorted = filterPages(withTimestamps).toSorted((a, b) => (b.updated_at || '').localeCompare(a.updated_at || ''))
  // The 12-cap is a "glimpse" for the unfiltered home grid. When a
  // category filter is active the user explicitly asked to see that
  // category, so show everything — capping silently hid entries
  // (e.g. 18 memory entries rendering only 12 cards).
  return activeFilter.value ? sorted : sorted.slice(0, RECENT_PAGE_LIMIT)
})

// On mobile the sidebar is hidden by default on `/` so HomeLanding is
// the primary view. Surfaces a "Browse catalog" affordance
// here so the user can still reach the catalog tree without first
// navigating into a page. Host wires this to `showSidebarOnMobile`.
const emit = defineEmits<{
  (e: 'open-catalog'): void
}>()

// Inline FTS search so the user can search without first opening the
// sidebar. Owns its own `useSidebarSearch` instance — the sidebar's
// search state stays independent (the user might be browsing the tree
// AND searching from the landing for two different things). Selecting
// a result navigates to the page; the route watch in usePageSelection
// handles "after select" mobile sidebar state so we don't coordinate
// that here. Destructuring keeps the ref identity AND lets the
// template auto-unwrap (no `search.X.value` clutter).
// Passing `activeFilter` scopes the search to the active category chip
// — when a filter is on, the search bar searches WITHIN
// those documents rather than wiki-wide. The chip strip therefore stays
// visible during search (see template) so the scope is legible and the
// user can change or clear it without leaving search mode.
const router = useRouter()
const search = useSidebarSearch(activeFilter)
const {
  query: searchQuery,
  results: searchResults,
  searching: searchPending,
  error: searchError,
  isSearchMode,
  runSearch,
  clear: clearSearch,
} = search

function onSearchSelect(path: string): void {
  router.push(`/p/${path}`)
}
</script>

<template>
  <section class="pt-2 pb-8" data-testid="home-landing">
    <div class="flex items-center justify-between gap-3 mb-1">
      <h2 class="text-xl font-semibold text-white m-0">Welcome back</h2>
      <!-- Mobile only — desktop already shows the sidebar permanently
           on the left. Sits beside the heading so the affordance is in
           the user's first reading position; matches the "← Back"
           button style on the page-meta bar so the two controls feel
           like one family of "show the sidebar" actions. -->
      <button
        type="button"
        class="hidden max-sm:flex items-center gap-1 shrink-0 text-sm text-white/60 hover:text-white transition-colors"
        data-testid="open-catalog"
        aria-label="Open catalog"
        @click="emit('open-catalog')"
      >
        <span aria-hidden="true">☰</span>
        <span>Browse catalog</span>
      </button>
    </div>
    <p v-if="!isSearchMode" class="text-sm text-white/50 m-0 mb-4">
      Pick up where you left off, or browse by category.
    </p>

    <!-- Inline FTS search. Same composable as the sidebar's search;
         own instance so the two queries don't shadow each other.
         The shared input + min-chars hint live in WikiSearchPanel. -->
    <div class="mb-6" data-testid="home-search">
      <WikiSearchPanel :search="search" placeholder="Search the brain..." />
    </div>

    <!-- Filter chips stay visible during search so the
         active scope is legible and adjustable — the search query below
         searches WITHIN the selected category rather than wiki-wide. -->
    <section v-if="categoryChips.length" class="mb-6">
      <h3 class="text-xs font-semibold uppercase tracking-widest text-white/40 m-0 mb-3">Filter by category</h3>
      <ul class="list-none p-0 m-0 flex flex-wrap gap-2" data-testid="category-chips">
        <li v-for="chip in categoryChips" :key="chip.raw">
          <button
            type="button"
            :aria-pressed="activeFilter === chip.raw"
            data-testid="category-chip"
            class="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm transition-colors"
            :class="
              activeFilter === chip.raw
                ? 'border-brand-primary/60 bg-brand-primary/10 text-white'
                : 'border-white/10 hover:border-white/30 hover:bg-white/[0.04] text-white/80 hover:text-white'
            "
            @click="toggleFilter(chip.raw)"
          >
            <span>{{ chip.display }}</span>
            <span class="text-xs" :class="activeFilter === chip.raw ? 'text-brand-primary' : 'text-white/40'">{{
              chip.count
            }}</span>
          </button>
        </li>
      </ul>
    </section>

    <!-- Scope hint: when a filter is active during search, make the
         "searching within X" relationship explicit. -->
    <p v-if="isSearchMode && activeFilterLabel" class="text-xs text-white/50 m-0 mb-3" data-testid="search-scope">
      Searching in
      <span class="text-white/70">{{ activeFilterLabel }}</span>
    </p>

    <!-- Search-mode results render below the chips so the user can
         re-scope (or clear) the filter without leaving search. Selecting
         a result routes to the page, clearing search via usePageSelection. -->
    <WikiSidebarSearchResults
      v-if="isSearchMode"
      :results="searchResults"
      :searching="searchPending"
      :error="searchError"
      :query="searchQuery"
      :active-filter-label="activeFilterLabel"
      @select-path="onSearchSelect"
      @retry="runSearch(searchQuery.trim())"
      @clear="clearSearch"
      @clear-filter="clearFilter"
    />

    <section v-if="!isSearchMode && (recentPages.length || activeFilter)">
      <h3 class="text-xs font-semibold uppercase tracking-widest text-white/40 m-0 mb-3 flex items-baseline gap-2">
        <span>Recently updated</span>
        <span
          v-if="activeFilterLabel"
          class="normal-case tracking-normal text-white/60 text-[11px] font-normal"
          data-testid="active-filter-label"
          >in {{ activeFilterLabel }}</span
        >
      </h3>

      <!-- Defensive: a category present in the chip strip is, by
           construction, non-empty in the catalog — but `recentPages`
           also requires `updated_at` to be non-blank, so a category
           whose pages were all created without a timestamp narrows
           to zero here. Surface that instead of leaving an empty
           heading, and give the user a one-click escape. -->
      <p v-if="recentPages.length === 0" class="text-sm text-white/50 m-0" data-testid="filter-empty-state">
        No recently-updated pages in {{ activeFilterLabel }} yet.
        <button type="button" class="text-brand-primary hover:underline" @click="clearFilter">Clear filter</button>
      </p>

      <ul
        v-if="recentPages.length"
        class="list-none p-0 m-0 grid gap-3 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3"
        data-testid="recent-pages"
      >
        <WikiPageCard
          v-for="page in recentPages"
          :key="page.id"
          :path="page.path"
          :title="page.title"
          :updated-at="page.updated_at"
          :tag="page.category ? categoryLabel(page.category) : null"
          :entity-type="page.entity_type"
        />
      </ul>
    </section>
  </section>
</template>
